/**
 * Program 12.1 UX3 — Early pursuit brief signals (deterministic, not AI authority).
 */
import type { PursuitWorkspaceViewModel } from './pursuitWorkspaceModel';
import { parseEpcContext, weightedPipeline } from './epcContextTypes';

export type PursuitBriefSignalKind = 'FACT' | 'CALCULATION' | 'MISSING' | 'INFERENCE';

export type PursuitBriefSignal = {
  id: string;
  kind: PursuitBriefSignalKind;
  label: string;
  detail?: string;
};

export function buildEarlyPursuitBriefSignals(vm: PursuitWorkspaceViewModel): PursuitBriefSignal[] {
  const signals: PursuitBriefSignal[] = [];
  const opp = vm.opportunity;

  signals.push({
    id: 'stage',
    kind: 'FACT',
    label: 'Current stage',
    detail: vm.commercialSummary.stageLabel,
  });

  if (opp?.customer_name) {
    signals.push({
      id: 'client',
      kind: 'FACT',
      label: 'Client',
      detail: opp.customer_name,
    });
  }

  if (opp?.lead_id) {
    signals.push({
      id: 'lead-lineage',
      kind: 'FACT',
      label: 'Lead lineage',
      detail: `Converted from lead ${opp.lead?.lead_number || opp.lead_id}`,
    });
  }

  const ctx = parseEpcContext((opp as { epc_context_json?: unknown } | null)?.epc_context_json);
  if (ctx.end_user) {
    signals.push({ id: 'end-user', kind: 'FACT', label: 'End user', detail: ctx.end_user });
  } else {
    signals.push({ id: 'end-user-missing', kind: 'MISSING', label: 'End user not captured' });
  }

  if (opp?.estimated_value && Number(opp.estimated_value) > 0) {
    const w = weightedPipeline(opp.estimated_value, opp.probability);
    signals.push({
      id: 'weighted',
      kind: 'CALCULATION',
      label: 'Weighted pipeline',
      detail: `value × probability (${opp.probability ?? 0}%)`,
    });
    if (w != null) {
      signals.push({
        id: 'weighted-value',
        kind: 'CALCULATION',
        label: 'Weighted amount',
        detail: String(w),
      });
    }
  } else {
    signals.push({ id: 'value-missing', kind: 'MISSING', label: 'Opportunity value not established' });
  }

  if (ctx.scope_summary) {
    signals.push({ id: 'scope', kind: 'FACT', label: 'Scope summary captured' });
  }

  for (const a of vm.attention.slice(0, 4)) {
    signals.push({
      id: `attention-${a.id}`,
      kind: a.severity === 'critical' ? 'INFERENCE' : 'FACT',
      label: a.title,
      detail: a.detail,
    });
  }

  return signals;
}
